/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ChevronRight,
  Clock,
  Calendar as CalendarIcon,
  BookOpen,
  BarChart2,
  ShoppingBag,
  CreditCard,
  FileText,
  CalendarDays
} from "lucide-react";
import "../../styles/studentDashboard.css";
import { getApiUrl } from "../../config/api";
import { useTenant } from "../../context/TenantContext";
import { useActiveLearner } from "../../hooks/useActiveLearner";
import { getStudentAuthHeaders, syncActiveLearner } from "../../utils/studentSession";

const StudentDashboard: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const { learners, activeLearner, activeLearnerId } = useActiveLearner();
  const userName = activeLearner?.name || localStorage.getItem("userName") || "Student";

  const [loading, setLoading] = useState(true);
  const [_subjects, setSubjects] = useState<any[]>([]);
  const [timetable, setTimetable] = useState<any[]>([]);
  const [transportInfo, setTransportInfo] = useState<{ mode: string; route?: any | null } | null>(null);
  const { hasModule } = useTenant();
  // Get today's day of week in uppercase (MONDAY, TUESDAY, etc.)
  const getTodayDayOfWeek = () => {
    const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    return days[new Date().getDay()];
  };
  // Format time from ISO string to HH:MM format
  const formatTime = (timeString: string) => {
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch {
      // If it's already in HH:MM format, return as is
      return timeString;
    }
  };
  // Filter and sort today's schedule
  const getTodaySchedule = () => {
    const today = getTodayDayOfWeek();
    const todaySchedule = timetable
      .filter(item => {
        const dayOfWeek = item.dayOfWeek?.toUpperCase();
        return dayOfWeek === today && !item.isBreak;
      })
      .sort((a, b) => {
        const dateA = new Date(a.startTime);
        const dateB = new Date(b.startTime);
        const minutesA = dateA.getHours() * 60 + dateA.getMinutes();
        const minutesB = dateB.getHours() * 60 + dateB.getMinutes();
        return minutesA - minutesB;
      });
    return todaySchedule;
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [subjectsRes, timetableRes, transportRes] = await Promise.all([
          axios.get(getApiUrl("academics/student/subjects"), { headers: getStudentAuthHeaders(token) }),
          axios.get(getApiUrl("academics/student/timetable"), { headers: getStudentAuthHeaders(token) }).catch(() => ({ data: [] })),
          axios.get(getApiUrl("users/me/transport"), { headers: getStudentAuthHeaders(token) }).catch(() => ({ data: null }))
        ]);
        setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : []);
        setTimetable(Array.isArray(timetableRes.data) ? timetableRes.data : []);
        setTransportInfo(transportRes?.data?.transport || null);
      } catch (err) {
        // Error fetching dashboard data
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchDashboardData();
  }, [token, activeLearnerId]);

  const todaySchedule = getTodaySchedule();
  const canUse = (moduleKey?: string) => !moduleKey || hasModule(moduleKey);
  const quickLinks = [
    { to: "/my-grades", label: "My Grades", Icon: BarChart2, iconClass: "text-teal-600 group-hover:text-white", boxClass: "bg-teal-50 group-hover:bg-teal-500", hoverClass: "hover:border-teal-400 hover:shadow-teal-500/5", module: "results" },
    { to: "/student/timetable", label: "Timetable", Icon: CalendarIcon, iconClass: "text-blue-600 group-hover:text-white", boxClass: "bg-blue-50 group-hover:bg-blue-500", hoverClass: "hover:border-blue-400 hover:shadow-blue-500/5", module: "timetable" },
    { to: "/student/attendance", label: "Attendance", Icon: Clock, iconClass: "text-purple-600 group-hover:text-white", boxClass: "bg-purple-50 group-hover:bg-purple-500", hoverClass: "hover:border-purple-400 hover:shadow-purple-500/5", module: "attendance" },
    { to: "/student/subjects", label: "Subjects", Icon: BookOpen, iconClass: "text-amber-600 group-hover:text-white", boxClass: "bg-amber-50 group-hover:bg-amber-500", hoverClass: "hover:border-amber-400 hover:shadow-amber-500/5", module: "classes" },
    { to: "/student/reports", label: "Report Cards", Icon: FileText, iconClass: "text-rose-600 group-hover:text-white", boxClass: "bg-rose-50 group-hover:bg-rose-500", hoverClass: "hover:border-rose-400 hover:shadow-rose-500/5", module: "report_cards" },
    { to: "/student/calendar", label: "School Calendar", Icon: CalendarDays, iconClass: "text-indigo-600 group-hover:text-white", boxClass: "bg-indigo-50 group-hover:bg-indigo-500", hoverClass: "hover:border-indigo-400 hover:shadow-indigo-500/5" },
    { to: "/student/fees", label: "Fees & Payments", Icon: CreditCard, iconClass: "text-emerald-600 group-hover:text-white", boxClass: "bg-emerald-50 group-hover:bg-emerald-500", hoverClass: "hover:border-emerald-400 hover:shadow-emerald-500/5", module: "fees" },
    { to: "/student/uniform", label: "Uniform Shop", Icon: ShoppingBag, iconClass: "text-sky-600 group-hover:text-white", boxClass: "bg-sky-50 group-hover:bg-sky-500", hoverClass: "hover:border-sky-400 hover:shadow-sky-500/5", module: "uniform_orders" }
  ].filter(({ module }) => canUse(module));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Main Content Area */}
      <div className="dashboard-main">
        {/* Welcome Section */}
        <section className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800">Welcome {userName}, stay on top of your academic progress.</h1>
          <p className="text-slate-500 mt-1 uppercase text-xs font-black tracking-widest">Welcome back to your academic portal</p>
          {activeLearner?.className && (
            <p className="text-slate-400 mt-3 text-sm font-semibold">Active learner: {activeLearner.name} • {activeLearner.className}</p>
          )}
        </section>

        {learners.length > 1 && (
          <section className="dashboard-section mb-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-bold text-slate-800">My Learners</h3>
                  <p className="text-xs text-slate-500">Switch dashboard context without signing out.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {learners.map((learner) => {
                  const isActive = learner.id === activeLearnerId;
                  return (
                    <button
                      key={learner.id}
                      type="button"
                      onClick={() => syncActiveLearner(learner.id)}
                      className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                        isActive
                          ? "border-teal-200 bg-teal-50 shadow-sm shadow-teal-100"
                          : "border-slate-200 bg-slate-50/80 hover:border-slate-300 hover:bg-white"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className={`text-[10px] font-black uppercase tracking-[0.18em] ${
                            isActive ? "text-teal-600" : "text-slate-400"
                          }`}>
                            {isActive ? "Current learner" : "Linked learner"}
                          </p>
                          <h4 className="mt-1 text-sm font-bold text-slate-800 truncate">{learner.name}</h4>
                          <p className="text-xs text-slate-500 truncate">{learner.className || "Class not assigned"}</p>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${
                            isActive ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {isActive ? "Active" : "Switch"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Quick Links Section */}
        <section className="dashboard-section">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {quickLinks.map(({ to, label, Icon, iconClass, boxClass, hoverClass }) => (
              <Link key={label} to={to} className={`p-6 bg-white rounded-2xl border border-slate-100 transition-all group text-center ${hoverClass}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 transition-colors ${boxClass}`}>
                  <Icon size={24} className={`${iconClass} transition-colors`} />
                </div>
                <h4 className="text-sm font-bold tracking-wide text-slate-700">{label}</h4>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {/* Right Sidebar */}
      <div className="dashboard-right">
        {/* Transport Widget */}
        {canUse("transport") && <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/50 mb-6 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-28 h-28 bg-amber-500/5 rounded-full -mr-10 -mt-10"></div>
          <div className="flex items-center justify-between mb-4 relative">
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-1">Transport</h3>
              <h4 className="text-lg font-bold text-slate-800">Route Details</h4>
            </div>
          </div>
          {transportInfo?.mode === "school" && transportInfo.route ? (
            <div className="space-y-3 relative">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-black">Route</span>
                <span className="text-sm font-bold text-slate-700">{transportInfo.route.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-black">Driver</span>
                <span className="text-sm font-bold text-slate-700">{transportInfo.route.driverName || "Assigned"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-black">Contact</span>
                <span className="text-sm font-bold text-slate-700">{transportInfo.route.driverContact || "-"}</span>
              </div>
              {transportInfo.route.fare !== undefined && transportInfo.route.fare !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-slate-400 font-black">Fare</span>
                  <span className="text-sm font-bold text-slate-700">K {Number(transportInfo.route.fare).toFixed(2)}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm font-bold text-slate-600 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3">
              Personal transport (no school route selected).
            </div>
          )}
        </div>}

        {/* Timetable Widget */}
        {canUse("timetable") && <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/50 mb-6 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/5 rounded-full -mr-16 -mt-16"></div>
          <div className="flex items-center justify-between mb-6 relative">
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-1">Schedule</h3>
              <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                Today's Classes
              </h4>
            </div>
            <div className="bg-teal-50 px-3 py-1 rounded-full">
              <span className="text-[10px] font-black text-teal-600 uppercase tracking-widest">{getTodayDayOfWeek()}</span>
            </div>
          </div>

          {todaySchedule.length > 0 ? (
            <div className="space-y-4 relative">
              {todaySchedule.map((item, idx) => {
                const subjectName = item.subjectName || item.subject?.name || 'Subject';
                const startTime = formatTime(item.startTime);
                const endTime = formatTime(item.endTime);
                return (
                  <div key={idx} className="flex gap-4 group">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 rounded-full bg-teal-500 group-hover:scale-150 transition-transform"></div>
                      {idx !== todaySchedule.length - 1 && <div className="w-0.5 flex-1 bg-slate-100 my-1"></div>}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex justify-between items-start mb-1">
                        <p className="font-black text-[13px] text-slate-800 uppercase tracking-tight">{subjectName}</p>
                        <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-lg border border-slate-100 capitalize">Upcoming</span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500 font-bold">
                        <div className="flex items-center gap-1.5">
                          <Clock size={12} className="text-teal-500" />
                          <span>{startTime} - {endTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <Link to="/student/timetable" className="block text-center p-3 mt-2 bg-slate-50 border border-slate-100 rounded-2xl text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 hover:bg-slate-100 transition-colors">
                View Full Timetable
              </Link>
            </div>
          ) : (
            <div className="text-center py-10 relative">
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CalendarIcon size={32} className="text-slate-200" />
              </div>
              <p className="text-sm font-bold text-slate-600 mb-1">No classes today</p>
              <p className="text-xs text-slate-400 mb-4 px-4">Enjoy your break! Check your full timetable for other days.</p>
              <Link to="/student/timetable" className="inline-flex items-center gap-2 bg-teal-500 text-white px-6 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-teal-600 transition-all shadow-lg shadow-teal-500/20">
                Full Schedule
              </Link>
            </div>
          )}
        </div>}

        {/* Quick Stats */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h3 className="text-lg font-bold mb-4">Quick Stats</h3>
          <div className="space-y-4">
            {canUse("results") && <Link to="/my-grades" className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <span className="text-sm font-medium text-slate-700">View Grades</span>
              <ChevronRight size={16} className="text-slate-400" />
            </Link>}
            {canUse("attendance") && <Link to="/student/attendance" className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <span className="text-sm font-medium text-slate-700">Attendance Record</span>
              <ChevronRight size={16} className="text-slate-400" />
            </Link>}
            {canUse("classes") && <Link to="/student/subjects" className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <span className="text-sm font-medium text-slate-700">My Subjects</span>
              <ChevronRight size={16} className="text-slate-400" />
            </Link>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;


